import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { map } from 'lodash'
import omit from 'lodash/omit'
import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import api from '@galo-monitor/services/apis/galoMonitorApi'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { FarmActions } from '@galo-monitor/stores/farm'
import colors from '@galo-monitor/styles/colors'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import Screen from '@galo-monitor/web-components/Screen'
import FarmModal from '@galo-monitor/web-containers/modals/company/FarmModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const FarmsScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()
  const tableRef = useRef(null)

  const [filtersValues, setFiltersValues] = useState({})
  const [queryParams, setQueryParams] = useState({
    page: 1
  })

  const { data: segments, mutate: mutateSegments } = useGaloMonitorApi('/companies/farms/segments')
  const { data: plans } = useGaloMonitorApi('/companies/plans')

  const segmentsOptions = useMemo(
    () => segments?.length > 0
      ? map(segments, ({ id, name }) => ({
        value: id.toString(),
        label: name
      }))
      : [],
    [segments]
  )

  const plansOptions = useMemo(
    () => plans?.length > 0
      ? map(plans, ({ id, name }) => ({
        value: id.toString(),
        label: name
      }))
      : [],
    [plans]
  )

  const asyncFarmData = useCallback(
    params => api.get(
      '/companies/farms/accounts',
      {
        params
      }
    ),
    []
  )

  const onChangeFilterValue = (newValue, filter) =>
    setFiltersValues(
      prevState => ({
        ...prevState,
        [filter]: newValue
      })
    )

  const userActions = useMemo(
    () => getActionsBySlug(routes.farms.slug),
    [getActionsBySlug, routes.farms.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('account', { howMany: 1 }),
        field: 'name',
        render: columnData => (
          <Link
            to={ `/farms/${ columnData.id }` }
            style={ {
              color: colors.primary, textDecoration: 'underline'
            } }
          >
            {columnData.name}
          </Link>
        )
      },
      {
        title: `${ t('responsible') } (${ t('client') })`,
        field: 'nameResponsible'
      },
      {
        title: t('segment', { howMany: 1 }),
        field: 'segment.name'
      },
      {
        title: t('plan', { howMany: 1 }),
        field: 'plan.name'
      }
    ],
    [t]
  )

  const filters = useMemo(() => [
    {
      id: 'segment',
      name: 'segment',
      label: t('segment', { howMany: 1 }),
      options: segmentsOptions,
      value: filtersValues.segment,
      onChange: newValue => onChangeFilterValue(newValue, 'segment'),
      customAutocompleteStyles: {
        flex: '0 0 160px'
      }
    },
    {
      id: 'plan',
      name: 'plan',
      label: t('plan', { howMany: 1 }),
      options: plansOptions,
      value: filtersValues.plan,
      onChange: newValue => onChangeFilterValue(newValue, 'plan'),
      customAutocompleteStyles: {
        flex: '0 0 160px'
      }
    }
  ], [filtersValues, plansOptions, segmentsOptions, t])

  const onError = useCallback(
    err => snackbar.error(
      t(err?.response?.data?.message || 'unexpected error occurred'), {
        persist: false
      }),
    [snackbar, t]
  )

  const onSuccess = useCallback(() => {
    tableRef.current.onQueryChange()
    mutateSegments()
  }, [mutateSegments])

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-farm',
        Component: FarmModal,
        props: {
          onSuccess
        }
      })
    },
    [createDialog, onSuccess]
  )

  const onEditClick = useCallback(
    (_, farm) => {
      createDialog({
        id: 'edit-farm',
        Component: FarmModal,
        props: {
          farm,
          onSuccess
        }
      })
    },
    [createDialog, onSuccess]
  )

  const onSoftDeleteClick = useCallback(
    (_, farm) => {
      const actionTitle = farm.isActive ? {
        present: t('inactivate'),
        past: t('inactivated', { gender: 'female', howMany: 1 })
      } : {
        present: t('activate'),
        past: t('activated', { gender: 'female', howMany: 1 })
      }

      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('farm', { howMany: 1 }),
            action: actionTitle.present,
            name: farm.name
          }),
          buttonTitle: actionTitle.present,
          buttonProps: {
            color: farm.isActive ? 'error' : 'primary'
          },
          onConfirm: () => {
            dispatch(FarmActions.inactivateFarm(
              farm,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('farm', { howMany: 1 }),
                  action: toLower(actionTitle.past)
                }))
                onSuccess()
              },
              onError
            ))
          }
        }
      })
    },
    [createDialog, dispatch, onError, onSuccess, snackbar, t]
  )

  const onHardDeleteClick = useCallback(
    (_, farm) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('farm', { howMany: 1 }),
            action: toLower(t('datagrid body permanently delete tooltip')),
            name: farm.name
          }),
          buttonTitle: t('datagrid body delete everything tooltip'),
          buttonProps: {
            color: 'error'
          },
          onConfirm: () => {
            dispatch(FarmActions.removeFarm(
              farm,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('farm', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
              },
              onError
            ))
          }
        }
      })
    },
    [createDialog, dispatch, onError, snackbar, t]
  )

  const onSoftDeleteChange = useCallback(
    filterInactive => {
      if(filterInactive) {
        setQueryParams(prevState => ({ ...prevState, active: false }))
        return
      }
      setQueryParams(omit(queryParams, 'active'))
    },
    [queryParams]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          row => ({
            icon: DataTableIcons.Edit,
            tooltip: t('datagrid body edit tooltip'),
            onClick: onEditClick,
            iconButtonProps: {
              size: 'small',
              ...(!row.isActive ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      if (userActions.delete) {
        datatableActions.push(
          row => ({
            icon: row.isActive
              ? DataTableIcons.Delete
              : () => <DataTableIcons.Reply style={ { color: colors.red } } />,
            tooltip: row.isActive ? t('datagrid body inactivate tooltip') : t('datagrid body activate tooltip'),
            onClick: onSoftDeleteClick,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          }),
          row => ({
            icon: () => <DataTableIcons.DeleteForever style={ { color: colors.red } } />,
            tooltip: t('datagrid body delete everything tooltip'),
            onClick: onHardDeleteClick,
            iconButtonProps: {
              size: 'small',
              ...(row.isActive ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [onEditClick, onHardDeleteClick, onSoftDeleteClick, t, userActions.delete, userActions.edit]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.farms.title,
      key: 'farms-screen',
      url: '/farms',
      active: true
    }
  ], [routes.farms.title])

  return (
    <Screen title={ routes.farms.title } breadcrumbs={ crumbs }>
      <DataTable
        tableRef={ tableRef }
        data={ asyncFarmData }
        columns={ columns }
        filters={ filters }
        actions={ actions }
        onAddClick={ userActions.write && onAddClick }
        disabledRow={ rowData => !rowData.isActive }
        onSoftDeleteChange={ onSoftDeleteChange }
        customActionsAtLast
        softDelete
      />
    </Screen>
  )
}

export default FarmsScreen
